import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { F8916Service } from './f8916.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';

@Component({
    selector: 'gtw-f8916',
    templateUrl: './f8916.component.html',
    styleUrls: ['./f8916.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F8916Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  baseGlobalParams: { 
    jcd_key: string; 
    tax_year: string; 
    scenario: string; 
    client_key: string; 
    issue_id: string, 
    is_issue_key_enabled: string; 
    scenarioCode: string,
    screen_key: string
  } | undefined;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Component properties
  modalTitle = "Reconciliation of Schedule M-3 Taxable Income With Tax Return Taxable Income for Mixed Groups";
  originalData: any[] = [];
  userSettings: any;
  logged_in_user: string = '';
  leId: string = '';
  formName = '1120';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  showFormLoader = true;
  minimise = false;
  crudLoading = false;
  isSaveClicked = false;
  currentTabName = "Form Entry";
  
  entityList: any[] = [];
  f8916FormView: any = { rows: [] };
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Row data from AngularJS
  rowData: any;

  // Tab configuration (like original AngularJS)
  tabs = [
    { id: 1, name: "Form Entry", active: true }
  ];
  activeTab = 1;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f8916Service: F8916Service,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      console.log('F8916 init() called', {
        hasGlobalService: !!this.globalService,
        hasGENERAL_CONFIG: !!this.GENERAL_CONFIG,
        hasRowData: !!this.rowData,
        hasFilterParams: !!this.filterParams
      });

      if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData) {
        console.log('F8916 init: Missing required data, skipping...');
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      console.log('F8916 init: All data available, starting setup...');
      this.setupEntityData();
      this.fetch8916records();
    } catch (e) {
      console.error('Error in F8916 init:', e);
    }
  }

  private setupEntityData() {
    const rowData = this.rowData;
    console.log('F8916 setupEntityData - rowData:', rowData);
    
    this.leId = rowData.LEID;
    this.cdr_no = rowData.MARS;
    this.leType = rowData.LE_TYPE;

    this.entityList = [
      {key: "Tax Year", value: rowData.TAX_YEAR},
      {key: "LEID", value: rowData.LEID},
      {key: "LE Name", value: rowData.LE_NAME}
    ];
    
    console.log('F8916 setupEntityData - entityList:', this.entityList);
  }

  fetch8916records() {
    const rowData = this.rowData;
    const params = {
      "action_id": 31333,
      "tax_year": rowData.TAX_YEAR,
      "scenario": rowData.SCENARIO,
      "jcd_key": rowData.JCD_KEY,
      "combination_key": rowData.COMBINATION_KEY,
      "form_name": "8916",
      "schedule_name": "NA",
      "part_no": "NA"
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      console.log('F8916 API Response:', data);
      
      if (data && data.irsForm && data.irsForm.formItemList) {
        this.processFormData(data);
        this.originalData = _.cloneDeep(this.f8916FormView);
        console.log('F8916 processed data:', this.f8916FormView);
      } else {
        console.error('F8916: Invalid data structure received from API', data);
      }
      
      this.showFormLoader = false;
      this.cdr.markForCheck();
    }, (error) => {
      console.error('F8916 API Error:', error);
      this.showFormLoader = false;
      this.cdr.markForCheck();
    });
  }

  private processFormData(data: any) {
    this.f8916FormView.rows = [];
    console.log('F8916 processFormData - formItemList length:', data.irsForm.formItemList.length);
    
    for (let i = 1; i < data.irsForm.formItemList.length; i++) {
      const saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
      const rows = data.irsForm.formItemList[i].dataRowList[0].dataCellList.length;
      
      const tempDataObj: any = {
        num: data.irsForm.formItemList[i].dataRowList[0].dataCellList[0].name,
        description: data.irsForm.formItemList[i].dataRowList[0].dataCellList[1].name,
        is_editable: data.irsForm.formItemList[i].dataRowList[0].dataCellList[2].property.is_editable_cell
      };

      tempDataObj.line_order = saveParamDtls[8];
      tempDataObj.saveParams = saveParamDtls;

      if (rows > 2) {
        this.iterateArray(rows, data.irsForm.formItemList[i].dataRowList[0].dataCellList, tempDataObj);
      }
      this.f8916FormView.rows.push(tempDataObj);
    }
  }

  private iterateArray(count: number, array: any[], tempDataObj: any) {
    if (count === 3) {
      tempDataObj.lineAttrKey_a = array[2].key.split('~')[1];
      tempDataObj.attrib_name_a = array[2].key.split('~')[0];
      tempDataObj.attrib_order_a = array[2].key.split('~')[2];
      tempDataObj.formula = array[2].formula;
      
      if (array[2].value == null) {
        tempDataObj.value = 0;
      } else {
        tempDataObj.value = array[2].value;
      }
    }
  }

  validateLines(line?: any) {
    // Apply validation logic similar to original
    _.forEach(this.f8916FormView.rows, (a) => {
      if (a.value == undefined || isNaN(a.value)) {
        a.value = 0;
      }
    });

    // Line calculations based on original logic
    const rows = this.f8916FormView.rows;
    
    // Line 3 calculation
    if (rows.length > 9) {
      rows[9].value = (parseInt(rows[0].value) + parseInt(rows[1].value) + parseInt(rows[2].value) + 
                      parseInt(rows[3].value) + parseInt(rows[4].value) + parseInt(rows[5].value) + 
                      parseInt(rows[6].value) + parseInt(rows[7].value) + parseInt(rows[8].value));
    }
    
    // Line 5 calculation
    if (rows.length > 19) {
      rows[19].value = (parseInt(rows[10].value) + parseInt(rows[11].value) + parseInt(rows[12].value) + 
                       parseInt(rows[13].value) + parseInt(rows[14].value) + parseInt(rows[15].value) + 
                       parseInt(rows[16].value) + parseInt(rows[17].value) + parseInt(rows[18].value));
    }
    
    // Line 6 calculation
    if (rows.length > 20) {
      rows[20].value = (parseInt(rows[9].value) - parseInt(rows[19].value));
    }
    
    // Line 8 calculation
    if (rows.length > 22) {
      rows[22].value = (parseInt(rows[21].value) + parseInt(rows[20].value));
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;
    const formRowDataArray: any[] = [];
    const rowData = this.rowData;

    for (let i = 0; i < this.f8916FormView.rows.length; i++) {
      const row = this.f8916FormView.rows[i];
      const formRowData = {
        tax_year: rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: rowData.COMBINATION_KEY,
        group_obj_key: rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKey_a,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.num === '1' ? null : (row.value ? row.value.toString() : ''),
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: '',
        JSON_DATA: []
      };
      formRowDataArray.push(formRowData);
    }

    this.formData.editrows = [formRowDataArray];
    const mainData = [this.formData];

    this.f8916Service.save8916Form(this.GENERAL_CONFIG.base_url, mainData, rowData, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          this.crudLoading = false;
          this.isSaveClicked = false;
          if (response.callSuccess === "1" && response.errorMessage === "null") {
            // Show success snackBar
            this.gtwSnackbar.openSnackBar('Transactions saved successfully', 'gtw-snackbar--green', true, 4);
            
            // Emit using @Output (AngularJS will close modal automatically)
            this.requestGridUpdate.emit('refresh');
          } else {
            const errorMsg = response.errorMessage || 'Error occurred in saving the Transaction. If this continues please contact support';
            this.gtwSnackbar.openSnackBar(errorMsg, 'gtw-snackbar--red', true, 4);
          }
          this.cdr.markForCheck();
        },
                 (error: any) => {
           this.crudLoading = false;
           this.isSaveClicked = false;
           this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
           this.cdr.markForCheck();
         }
      );
  }

  reset() {
    this.f8916FormView = _.cloneDeep(this.originalData);
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  itemTracker(item: any): string {
    return item.line_order + '-' + item.attrib_order_a;
  }
}
